import type { LoadTranslationsQuery } from '#graphql-operations'
import type { GraphqlResponse } from '#nuxt-graphql-middleware/response'
import { mapDrupalTexts } from 'nuxt-easy-texts/drupal'
import { defineEasyTextsLoader } from 'nuxt-easy-texts/loader'

type MappedTranslations = ReturnType<typeof mapDrupalTexts>

function mapTranslations(
  response: GraphqlResponse<LoadTranslationsQuery>,
): MappedTranslations {
  return mapDrupalTexts(response.data.translations)
}

export default defineEasyTextsLoader(() => {
  const appLanguage = useCurrentLanguage()
  return {
    async load(language) {
      // During prerendering, we don't have Drupal available. The prerendered
      // pages don't need any texts, so this is fine.
      // If this option is missing, the build in the pipeline will fail during
      // prerendering with a Javascript Heap memory error.
      if (import.meta.prerender) {
        return {}
      }
      // With caching on the server.
      if (import.meta.server) {
        const event = useRequestEvent()
        const { value, addToCache } = await useDataCache<MappedTranslations>(
          'translations_' + language,
          event,
        )

        if (value) {
          return value
        }

        const data = await useGraphqlQuery('loadTranslations', null, {
          clientContext: {
            language,
            env: 'server',
          },
        })

        const mapped = mapTranslations(data)
        await addToCache(
          mapped,
          data.extensions?.cacheability?.tagsNuxt,
          60 * 60,
        )
        return mapped
      }

      return useGraphqlQuery('loadTranslations', null, {
        graphqlCaching: {
          client: true,
        },
        clientContext: {
          language,
        },
      }).then(mapTranslations)
    },
    currentLanguage() {
      return computed(() => appLanguage.value)
    },
  }
})
